/**
 * Slice 18 on screen — the tones, the headings, and the two sentences the
 * contract does not own.
 *
 * `campaign-display.ts` is the model for this file and the split is the same
 * one: **the contract writes the sentences, this writes the headings, and the
 * components write neither.** `CAMPAIGN_ISSUE_TITLE`'s own header states the
 * rule — "headings, not repairs" — because a heading restated as a repair gives
 * a person two accounts of one problem that can disagree. So every explanatory
 * sentence here comes out of `@wend/shared`: `listHealthVerdictCopy`,
 * `WAVE_HOLD_SENTENCES`, `CAMPAIGN_HALT_SENTENCES`, `warmUpNotNeededSentence`.
 *
 * ## THE ONE THING THAT MAKES A WHATSAPP CAMPAIGN SAFE HERE
 *
 * This repository's worst shipped defect was an AWS *email* threshold drawn
 * against a denominator that included WhatsApp — 1.55% behind a green badge on a
 * workspace sitting exactly on AWS's 5% line. The contract made that
 * unrepresentable in its own types; a screen can still reintroduce it by writing
 * the word "bounced" beside a number that is not one.
 *
 * So **every word this file puts next to a risk number is keyed on
 * `SendRiskGate.measure`, never on the channel and never on a literal.** The
 * only way to obtain a `SendRiskGate` is `sendRiskGateFor(channel)`, the only
 * way to obtain a `measure` is to hold a gate, and {@link SEND_RISK_MEASURE_LABEL}
 * is exhaustive over the two. A component cannot pair email's threshold with
 * WhatsApp's noun, because it never sees either separately: it passes a gate and
 * gets back a whole sentence. That is why {@link sendRiskStopSentence} and
 * {@link sendRiskCountSentence} take a gate rather than a rate and a word — a
 * signature with two loose arguments is a signature somebody eventually crosses.
 *
 * The nouns deliberately match `campaignHaltSentence`'s own ("bounced
 * permanently", "could not be reached") so that the sentence beside a running
 * campaign's counts and the sentence on its halt describe the same event in the
 * same words.
 *
 * **And there is exactly one sentence here that names a provider, so it is keyed
 * on the channel instead.** `WAVE_HOLD_SENTENCES` is keyed on the hold *reason*,
 * which is channel-free by design — "waiting for more delivery reports" is the
 * same fact on both. The escalated version of the silence branch is not: it says
 * which pipe has gone quiet, and that is SNS on email and the status webhook on
 * WhatsApp. It was one string for both and named Amazon's notifications, which
 * put an Amazon integration in front of a tenant sending through Meta — the same
 * defect as the threshold one, arriving through the one table that had no reason
 * to be per channel. `waveHoldSentenceNoReports(channel)` is now the only way to
 * obtain it, and {@link waveHoldReading} has the channel in hand already.
 *
 * ## WHY {@link waveHoldReading} EXISTS RATHER THAN A CALL TO `mayWaveRelease`
 *
 * `mayWaveRelease` is the gate, it is pure, and the obvious thing is to call it
 * here — that is how `isQuietHour` is re-derived on the schedule panel, and the
 * contract's whole premise is that both sides share one verdict.
 *
 * **Two of its four inputs are not on the wire.** `WaveReleaseContext` needs a
 * `SendRiskMeasurement` and `handedToProvider`, and `campaignListSafetySchema`
 * carries neither: it carries `reading`, which is the measurement already
 * projected through `rateReading` and explicitly there "so the screen shows
 * counts rather than a verdict". Rebuilding a measurement out of a reading and
 * guessing `handedToProvider` from the recipient counts would be a *second,
 * differently-derived* implementation of the gate — and the way two
 * implementations of "may this send" drift is that one of them stops checking.
 *
 * So this function does not decide anything. It **explains a hold that has
 * already been decided**, from the two facts the wire does carry: the wave
 * clock, and the reading. It answers with the contract's own
 * `WaveHoldReason` and the contract's own sentence, and its branch order
 * mirrors `mayWaveRelease`'s so the two cannot describe the same state
 * differently. Where it genuinely cannot separate two of the gate's branches —
 * the sample floor and the coverage floor — **both of them return
 * `too_few_delivery_reports` in `mayWaveRelease` too**, so the screen says
 * exactly what the gate would have said.
 *
 * **One branch is not `mayWaveRelease`'s and is deliberately kept**: the stale
 * plan. `decideWaveRelease` discovers it after the gate opens, when there is no
 * `pending` wave left to release, and answers `plan_exhausted`. The wire carries
 * the wave list, so this screen can see the same fact — and it has to, because
 * without it the function fell through to *"waiting for more delivery reports"*
 * on the one hold that never ends by itself.
 *
 * Nothing here releases a wave, and nothing here is consulted by anything that
 * does. The API owns both.
 *
 * ## AND IT FAILS CLOSED, LIKE EVERY OTHER GATE IN THIS SLICE
 *
 * The default branch is `unreadable`, written first rather than last. A missing
 * wave list, a wave whose clock will not parse, a reading that never arrived:
 * all of them say the campaign is holding and Wend could not read its plan.
 * None of them renders as "waiting a few more minutes", and none of them renders
 * a zero — a rate that was never measured is not a rate of nought, which is the
 * defect this codebase has already shipped.
 */
import {
  UNSETTLED_HOLD_ESCALATES_AFTER_MINUTES,
  WAVE_HOLD_SENTENCES,
  sendRiskGateFor,
  waveHoldSentenceNoReports,
  waveHoldUntil,
  type CampaignStatus,
  type CampaignWave,
  type CampaignWaveStatus,
  type Channel,
  type ListHealthFinding,
  type ListHealthReason,
  type ListHealthSeverity,
  type ListHealthVerdict,
  type RateReading,
  type SendRiskGate,
  type WarmUpPlan,
  type WaveHoldReason,
} from '@wend/shared';
import type { BadgeTone, CalloutTone } from '@wend/ui';

import { formatCount, pluralise } from './format';

/* -------------------------------------------------------------------------- */
/* List health                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The verdict's colour. Four bands, four tones, and `ready` is the only green.
 *
 * `warm_up_advised` is `info` rather than `warn`, and that is the same argument
 * `ISSUE_TONE` makes about the send-guard advisories: it is the **ordinary**
 * verdict for a list nobody has mailed, every first campaign will carry it, and
 * an amber triangle on the expected outcome is how a reader learns to scroll
 * past the one that is not expected. The contract's own copy agrees — its label
 * is "Send in batches", which is a plan rather than a problem.
 */
export const LIST_HEALTH_VERDICT_TONE: Readonly<Record<ListHealthVerdict, BadgeTone>> = {
  ready: 'good',
  warm_up_advised: 'info',
  high_risk: 'warn',
  do_not_send: 'bad',
};

/** One finding's colour, from its own severity. */
export const LIST_HEALTH_SEVERITY_TONE: Readonly<Record<ListHealthSeverity, CalloutTone>> = {
  note: 'info',
  concern: 'warn',
  severe: 'bad',
};

/** Worst first: the verdict is a count of the severe ones, so they lead. */
const SEVERITY_RANK: Readonly<Record<ListHealthSeverity, number>> = {
  severe: 0,
  concern: 1,
  note: 2,
};

export function sortFindings(findings: readonly ListHealthFinding[]): ListHealthFinding[] {
  return [...findings].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

/**
 * The heading above a finding's own sentence — **which of the eight it is, at a
 * glance, and nothing more.**
 *
 * The repair is the `message` the contract wrote, with this list's real counts
 * in it, and the screen prints that unchanged. Restating it here would be the
 * `CAMPAIGN_ISSUE_TITLE` mistake made deliberately: two accounts of one problem,
 * one of which will be edited without the other.
 *
 * **Keyed on the channel first**, for the reason
 * `LIST_HEALTH_SENTENCES_BY_CHANNEL` is: the sentence underneath is now written
 * per channel, and a heading that stayed channel-free would put *"Most of this
 * list is at one destination"* — a receiving system — above a sentence about a
 * dialling range, and *"Some of these have bounced before"* above one about
 * WhatsApp failures. Two accounts of one finding again, in one callout.
 *
 * Every entry is written out on both channels rather than overlaid on a base
 * record. Sixteen short strings is cheap; an override map is a place for a
 * heading to go missing, and a missing heading fails to email's.
 */
export const LIST_HEALTH_REASON_TITLE_BY_CHANNEL: Readonly<
  Record<Channel, Readonly<Record<ListHealthReason, string>>>
> = {
  email: {
    never_mailed: 'This list has not been sent to before',
    domain_concentration: 'Most of this list is at one destination',
    role_addresses: 'Some of these go to a desk, not a person',
    prior_hard_bounces: 'Some of these have bounced before',
    suppressed_present: 'Some of these have unsubscribed',
    syntactically_invalid: 'Some of these are not addresses',
    duplicates: 'Some rows repeat an address',
    nothing_to_send: 'There is nobody to send to',
  },
  whatsapp: {
    never_mailed: 'This list has not been messaged before',
    domain_concentration: 'Most of these numbers are on one range',
    // Unreachable on this channel — a phone number has no local part, so the
    // contract does not raise the finding at all. Present because the record is
    // exhaustive, and worded so that it would still be true if it ever were.
    role_addresses: 'Some of these are a shared line, not a person',
    prior_hard_bounces: 'Some of these have failed before',
    suppressed_present: 'Some of these have opted out',
    syntactically_invalid: 'Some of these are not phone numbers',
    duplicates: 'Some rows repeat a number',
    nothing_to_send: 'There is nobody to send to',
  },
};

/** The one supported way to obtain a heading. There is no channel-free version. */
export function listHealthReasonTitle(channel: Channel, reason: ListHealthReason): string {
  return LIST_HEALTH_REASON_TITLE_BY_CHANNEL[channel][reason];
}

/* -------------------------------------------------------------------------- */
/* Waves                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A wave's colour.
 *
 * `sent` is `info` and not `warn`: a batch that has gone out and is waiting for
 * results is the plan working exactly as designed, and it is the state a large
 * campaign spends most of its life in. Only `cleared` is green, because only
 * `cleared` means somebody looked at the numbers and they were fine.
 */
export const CAMPAIGN_WAVE_STATUS_TONE: Readonly<Record<CampaignWaveStatus, BadgeTone>> = {
  pending: 'neutral',
  releasing: 'info',
  sent: 'info',
  cleared: 'good',
};

/** The wave being handed to a provider right now, if one is. */
export function releasingWave(waves: readonly CampaignWave[]): CampaignWave | null {
  return waves.find((wave) => wave.status === 'releasing') ?? null;
}

/**
 * A wave whose recipients have all been attempted — the only two arms of
 * `campaignWaveSchema` that carry `sentAt`, and therefore the only two a hold
 * clock can be read from.
 *
 * Named as a type rather than checked at each use so the narrowing travels with
 * the value: a caller cannot reach `sentAt` on a wave that has not got one,
 * which is the discriminated union doing the job it was built for.
 */
export type FinishedWave = Extract<CampaignWave, { status: 'sent' | 'cleared' }>;

/**
 * The most recent wave whose hold clock has started — the one a hold is about.
 *
 * Highest wave number rather than last in the array: the API's ordering is not
 * something this screen should depend on, and a plan read back out of order
 * would otherwise time its hold against the wrong batch.
 */
export function lastFinishedWave(waves: readonly CampaignWave[]): FinishedWave | null {
  let latest: FinishedWave | null = null;
  for (const wave of waves) {
    if (wave.status !== 'sent' && wave.status !== 'cleared') continue;
    if (latest === null || wave.waveNumber > latest.waveNumber) latest = wave;
  }
  return latest;
}

/**
 * The heading and tone over a hold. **Headings, not repairs** — the sentence
 * underneath is always the contract's.
 *
 * It is a table rather than a constant because two of the seven reasons are not
 * waits at all, and the superseded panel titled every one of them *"Waiting
 * before the next batch"* in `info`. On `plan_exhausted` there is no next batch
 * and there never will be one; on `unreadable` Wend cannot read the plan it
 * would take the next batch from. Both need a person, and a heading that says
 * they are a wait is the same defect the sentence beneath them was just fixed
 * for — one state described two ways, with the reassuring one on top.
 *
 * `campaign_not_holding` is here for exhaustiveness and never rendered: the
 * panel only shows a hold on a holding campaign.
 */
export const WAVE_HOLD_HEADING: Readonly<
  Record<WaveHoldReason, { readonly title: string; readonly tone: CalloutTone }>
> = {
  hold_time_not_elapsed: { title: 'Waiting before the next batch', tone: 'info' },
  no_delivery_reports_yet: { title: 'Waiting before the next batch', tone: 'info' },
  too_few_delivery_reports: { title: 'Waiting before the next batch', tone: 'info' },
  rate_at_or_above_threshold: { title: 'Paused — too many failures so far', tone: 'warn' },
  campaign_not_holding: { title: 'Not waiting between batches', tone: 'info' },
  plan_exhausted: { title: 'Stopped — the batches are finished', tone: 'warn' },
  unreadable: { title: 'Stopped — the batch plan cannot be read', tone: 'warn' },
};

/** What a hold is waiting for, in the contract's own vocabulary. */
export interface WaveHoldReading {
  readonly reason: WaveHoldReason;
  /** The contract's sentence for that reason. Never composed here. */
  readonly message: string;
  /** The instant the time half of the gate opens, when time is what holds it. */
  readonly holdUntil: string | null;
}

export interface WaveHoldInput {
  readonly campaignStatus: CampaignStatus;
  /** Null when the plan or its rows could not be read. That holds. */
  readonly waves: readonly CampaignWave[] | null;
  /** Null when the rate could not be computed at all. That holds too. */
  readonly reading: RateReading | null;
  readonly channel: Channel;
  /** Injected so the branches are testable without waiting an hour. */
  readonly now?: Date;
}

/**
 * Why this campaign is still waiting — see the file header for why this
 * explains a decision rather than taking one.
 *
 * The branch order is `mayWaveRelease`'s, in the same sequence and with the same
 * reasons, so the screen and the gate cannot describe one state in two ways.
 */
export function waveHoldReading(input: WaveHoldInput): WaveHoldReading {
  const now = input.now ?? new Date();
  const unreadable: WaveHoldReading = {
    reason: 'unreadable',
    message: WAVE_HOLD_SENTENCES.unreadable,
    holdUntil: null,
  };

  if (input.campaignStatus !== 'holding_between_waves') {
    return {
      reason: 'campaign_not_holding',
      message: WAVE_HOLD_SENTENCES.campaign_not_holding,
      holdUntil: null,
    };
  }

  if (input.waves === null) return unreadable;

  const previous = lastFinishedWave(input.waves);
  if (previous === null) return unreadable;

  const holdUntil = waveHoldUntil(previous);
  if (holdUntil === null) return unreadable;

  if (now.getTime() < holdUntil.getTime()) {
    return {
      reason: 'hold_time_not_elapsed',
      message: WAVE_HOLD_SENTENCES.hold_time_not_elapsed,
      holdUntil: holdUntil.toISOString(),
    };
  }

  const reading = input.reading;
  if (reading === null) return unreadable;

  if (reading.denominator === 0) {
    // Three hours of silence changes the sentence and never the verdict: the
    // likely cause stops being "the mail is in flight" and becomes "the
    // notifications are not reaching Wend", which is a thing a person acts on.
    const sentAt = Date.parse(previous.sentAt);
    // Unreachable in practice — `waveHoldUntil` answers null on the same parse,
    // and that already returned above — but it fails to `unreadable` rather than
    // to a guess, because the guess here would be the escalated sentence and
    // that one accuses a working webhook of being broken.
    if (Number.isNaN(sentAt)) return unreadable;
    const silentMinutes = Math.max(0, (now.getTime() - sentAt) / 60_000);

    return {
      reason: 'no_delivery_reports_yet',
      message:
        silentMinutes >= UNSETTLED_HOLD_ESCALATES_AFTER_MINUTES
          ? // Per channel, and the only sentence in this file that names a
            // provider at all: what has gone quiet is SNS on email and the
            // status webhook on WhatsApp, and telling a Meta tenant to check
            // Amazon is the defect this whole file is arranged against.
            waveHoldSentenceNoReports(input.channel)
          : WAVE_HOLD_SENTENCES.no_delivery_reports_yet,
      holdUntil: null,
    };
  }

  // THE ONE PLACE THIS DEPARTS FROM `mayWaveRelease`'S ORDER, STATED RATHER THAN
  // HIDDEN. The gate tests coverage before the rate, so a wave that is both
  // under-reported and over the line is described by the gate as under-reported
  // and by this as over the line. Coverage is the input that is not on the wire,
  // so the order cannot be reproduced; both answers hold, and of the two facts
  // the rate is the one somebody can act on. In practice the branch is barely
  // reachable: `mustCampaignHalt` reads the same rate at every batch boundary
  // and moves the campaign to `halted_delivery_risk`, which is not a holding
  // status and is answered three branches above.
  const gate = sendRiskGateFor(input.channel);
  if (reading.rate !== null && reading.rate >= gate.haltRate) {
    return {
      reason: 'rate_at_or_above_threshold',
      message: WAVE_HOLD_SENTENCES.rate_at_or_above_threshold,
      holdUntil: null,
    };
  }

  /*
   * THE HOLD THAT NEVER ENDS, and the one branch that is this file's rather
   * than `mayWaveRelease`'s.
   *
   * `decideWaveRelease` asks this *after* the gate: a campaign is
   * `holding_between_waves` with no `pending` wave left when a segment was
   * re-materialised underneath it, so more recipients exist than the six
   * committed waves cover. The gate opens, there is nothing to release, and the
   * campaign sits there for ever.
   *
   * The superseded version had no branch for it and fell through to
   * `too_few_delivery_reports` — "waiting for more delivery reports", a
   * temporary sentence for a permanent state, on a screen whose only other
   * account of the campaign says it continues on its own. It is placed ahead of
   * that fall-through rather than after it because the fall-through is a *guess*
   * between the sample floor and the coverage floor, and this is a fact: when
   * the plan has nothing left, no quantity of delivery reports releases
   * anything, so the reader needs the action rather than the wait.
   */
  if (!input.waves.some((wave) => wave.status === 'pending')) {
    return {
      reason: 'plan_exhausted',
      message: WAVE_HOLD_SENTENCES.plan_exhausted,
      holdUntil: null,
    };
  }

  // Everything else the gate can be holding on is a question of how much has
  // answered — the sample floor, which this screen can see in `reading.rate`
  // being null, and the coverage floor, which it cannot see at all. Both return
  // `too_few_delivery_reports` from `mayWaveRelease`, so one branch is honest
  // for both rather than a guess between them.
  return {
    reason: 'too_few_delivery_reports',
    message: WAVE_HOLD_SENTENCES.too_few_delivery_reports,
    holdUntil: null,
  };
}

/* -------------------------------------------------------------------------- */
/* The risk numbers, and the words that may stand beside them                  */
/* -------------------------------------------------------------------------- */

/**
 * What a gate counts, said in English. **Keyed on the measure, never on the
 * channel** — see the file header.
 *
 * `pastTense` matches `campaignHaltSentence`'s own nouns, so a campaign's
 * running counts and its halt sentence describe the same event in the same
 * words.
 */
export interface SendRiskMeasureLabel {
  /** For a sentence: "…of these messages bounced permanently". */
  readonly pastTense: string;
}

export const SEND_RISK_MEASURE_LABEL: Readonly<
  Record<SendRiskGate['measure'], SendRiskMeasureLabel>
> = {
  permanent_bounce: { pastTense: 'bounced permanently' },
  undeliverable_number: { pastTense: 'could not be reached' },
};

/**
 * A fraction as a percentage, at the precision the rest of this app uses.
 *
 * Module-private. `CampaignResultsPanel`, `CampaignWhatsAppDelivery` and
 * `HomePage` each carry their own copy of this three-line function; exporting a
 * fourth that nobody imports would look like the shared one and be a fifth. The
 * components here never format a rate at all — they are handed whole sentences,
 * which is what keeps a threshold and a noun from being paired by hand.
 */
function formatRate(rate: number, decimals = 1): string {
  return `${(rate * 100).toFixed(decimals)}%`;
}

/**
 * A hold, in the words a person waiting for it would use.
 *
 * Hours when the number divides, minutes otherwise — "1 hour" reads as a wait
 * and "60 minutes" reads as a setting.
 */
export function formatHoldWait(minutes: number): string {
  if (minutes <= 0) return 'no wait';
  if (minutes % 60 === 0) return pluralise(minutes / 60, 'hour');
  return pluralise(minutes, 'minute');
}

/**
 * What stops the campaign, on the screen where somebody is deciding to schedule
 * it.
 *
 * It names the number **and which side of it is bad**, which is this codebase's
 * standing rule about percentages: "4%" means nothing on its own. It also names
 * the sample floor, because the floor is the reason a small campaign is not
 * stopped by its first unlucky message and a reader who does not know that will
 * distrust the first figure they see.
 */
export function sendRiskStopSentence(gate: SendRiskGate): string {
  const label = SEND_RISK_MEASURE_LABEL[gate.measure];
  return `Sending stops on its own once ${formatRate(gate.haltRate)} of the messages with a result ${label.pastTense}, and never on fewer than ${formatCount(gate.minimumSettledOutcomes)} results.`;
}

/**
 * What the schedule confirmation adds once a plan exists.
 *
 * **The confirmation was wrong without it.** It reads "this will send 526
 * messages as soon as the sender picks it up", which stopped being true the
 * moment a warm-up plan existed: 102 go out and the rest waits. A person
 * authorising a send on that sentence alone would think the campaign had stalled
 * an hour later — and the recorded failure mode of that belief is cancelling and
 * re-creating the campaign, which re-materialises the list and starts the ramp
 * again.
 *
 * It names the first batch and the first wait only. The whole plan is on the
 * card above the button, unfolded; repeating six lines of it inside a
 * confirmation is how a confirmation stops being read.
 */
export function warmUpFirstBatchSentence(plan: WarmUpPlan): string {
  const first = plan.waves[0];
  if (first === undefined) return '';
  return `It goes out in ${pluralise(plan.waves.length, 'batch', 'batches')} rather than all at once: ${formatCount(first.plannedSize)} first, then at least ${formatHoldWait(first.holdMinutes)} while Wend waits to see how they were received.`;
}

/**
 * What the schedule screen says when the list check itself would not load.
 *
 * A failed check is **not** a clean list, and the sentence has to say both
 * halves: this screen cannot tell you how the send will be split, and the send
 * still stops itself if it goes badly. The second half is true whatever this
 * read did, because `mustCampaignHalt` takes no plan and no size and therefore
 * applies at every volume — so leaving it out would overstate the loss.
 */
export const LIST_CHECK_UNAVAILABLE_SENTENCE =
  'Wend could not check this list, so this screen cannot say how the send would be split into batches. Sending still stops on its own if too many messages come back as failures.';

/**
 * The sentence for a reading that has not been taken.
 *
 * **A rate nobody could compute is not a rate of nought.** This app has shipped
 * the other thing, and a zero here would be read as "no bounces so far" on a
 * campaign whose delivery reports never arrived — which is the one state where
 * the reader most needs to go and look at something.
 */
export const SEND_RISK_NOT_MEASURED_SENTENCE =
  'Wend could not read the delivery results for this campaign, so there is no figure here — not a figure of zero.';

/**
 * The sentence for a reading whose denominator is genuinely still empty.
 *
 * **It names no provider**, and that is deliberate rather than vague: this
 * string is reached on either channel, and `CAMPAIGN_STATUS_DISPLAY.sending`
 * saying "Amazon SES" on a WhatsApp campaign is the wart slice 17 recorded
 * rather than a precedent to copy. The fact it needs to carry — results arrive
 * long after acceptance — is true of both, so it is said without naming either.
 */
export const SEND_RISK_NOTHING_SETTLED_SENTENCE =
  'No delivery results have come back yet. They arrive minutes to hours after a message is accepted, so this is the ordinary state of a batch that has just gone out — not a clean one.';

/**
 * The counts, and a percentage **only when the reading says one is allowed**.
 *
 * Below `minimumSettledOutcomes` the reading's `rate` is null and this prints
 * the two counts instead, with the floor named. That is the same rule the
 * contract applies to its own halt sentence: at a denominator of 51, "3.9%" is a
 * number this codebase forbids rendering, and "3 of 51" is true at any volume.
 */
export function sendRiskCountSentence(gate: SendRiskGate, reading: RateReading | null): string {
  if (reading === null) return SEND_RISK_NOT_MEASURED_SENTENCE;
  if (reading.denominator === 0) return SEND_RISK_NOTHING_SETTLED_SENTENCE;

  const label = SEND_RISK_MEASURE_LABEL[gate.measure];
  const counts = `${formatCount(reading.numerator)} of the ${formatCount(reading.denominator)} messages with a result so far ${label.pastTense}`;

  if (reading.rate === null) {
    return `${counts}. Under ${formatCount(reading.minimumVolume)} results Wend shows the counts rather than a percentage, because one message either way would move it by several points.`;
  }

  return `${counts} — ${formatRate(reading.rate)}. ${sendRiskStopSentence(gate)}`;
}
